import httpRequestHandler from 'src/utils/httpRequestHandler';
import CACHE_TIME from 'src/constants/cacheTime';
import cacheService from './cacheService';

class AccountService {
  /** @typedef {import('../services/cacheService').CacheService} CacheService */

  /** @type {CacheService} */
  #cacheService;

  constructor(cacheSvc) {
    this.#cacheService = cacheSvc;
  }

  setAxiosInterceptors = ({ onLogout }) => {
    httpRequestHandler.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          this.setSession(null);

          if (onLogout) {
            onLogout();
          }
        }

        return Promise.reject(error);
      }
    );
  };

  setSession = accessToken => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
      httpRequestHandler.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      localStorage.removeItem('accessToken');
      delete httpRequestHandler.defaults.headers.common.Authorization;
    }
  };

  getAccessToken = () => localStorage.getItem('accessToken');

  updateProfile = async update => {
    const { data } = await httpRequestHandler.post('/account/me', {
      user: update
    });
    this.setSession(data.accessToken);
    return data;
  };

  getDealerships = async (isSuperUser = false, fetch, offset, search, activeDealer) => {
    // activeDealerId is intentionally excluded from the cache key so callers
    // that omit it (e.g., DealerDialog, UsersView) share the same cached
    // first-page list as the App.js bootstrap call.
    const key = {
      accountService: 'getDealerships',
      isSuperUser,
      fetch,
      offset,
      search,
    };

    const shouldCache = !search && Number(offset || 0) === 0;
    let response = shouldCache ? this.#cacheService.getFromCache(key) : null;

    if (!response) {
      const { data } = await httpRequestHandler.post('/account/get-dealerships', {
        isSuperUser,
        fetch,
        offset,
        search,
        activeDealerId: activeDealer?.id,
      });
      response = data;
      if (shouldCache) {
        this.#cacheService.saveToCache(key, response, CACHE_TIME.FIFTEEN_MIN);
      }
    }
    return response;
  }

  /**
   * @param {{ user: {id: string, roles: [], dealerships: []}, companyCodes: string[] }} data 
   * @returns {Promise<*>}
   */
  assignDealerships = async ({ user, companyCodes }) => {
    const { data } = await httpRequestHandler.post('/account/assign-dealerships', {
      user,
      companyCodes,
    });
    return data;
  }

  getDealerUsers = async () => {
    const key = 'accountService:dealerUsers';
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      const { data } = await httpRequestHandler.get('/account/get-dealerships');
      response = data;
      this.#cacheService.saveToCache(key, response, CACHE_TIME.FIFTEEN_MIN);
    }
    return response;
  }

  getClaimsQoreAccountInfo = async (emailAddress, isSuperUser = false) => {
    const { data } = await httpRequestHandler.post('/account/get-cq-user', {
      emailAddress,
      isSuperUser
    });
    return data;
  };

  bulkRegisterDealers = async (file, shouldSendEmail) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await httpRequestHandler.post(
      `/account/bulk-register-dealers?confirmationEmail=${shouldSendEmail}`,
      formData
    );

    return response;
  }
}

const accountService = new AccountService(cacheService);
accountService.setSession(accountService.getAccessToken());

export default accountService;
